import {
  put, takeEvery, all, select, takeLatest,
} from 'redux-saga/effects';
import { bootstrap } from 'circe/actions';
import {
  change, doSubmit, startSubmit, stopSubmit,
} from 'circe/forms/actions';
import { paid, authorized, authorizedCallback } from 'domain/fuse/payments/actions';
import { getCurrentProfile } from 'domain/fuse/auth/selectors';
import { getCurrentAuthHeader } from 'domain/fuse/auth/utils';
import creditCardType from 'credit-card-type';
import { startLoading, doneLoading } from 'domain/layout/actions';
import { shippingClient } from 'domain/HttpClients';
import { changeLocale, initLocale } from 'circe/I18n/actions';

import * as I18n from 'circe/I18n';
import Logger from 'circe/logger';
import { pay, paying } from 'domain/payment/actions';
import { deprecate } from 'util';
import { ROUTES as ORDER_ROUTES } from 'domain/shipment/order/constants';
import { generatePath } from 'react-router';
import { show as showNotification } from 'circe/notifications/actions';
import { getCurrency } from 'domain/fuse/preferences/currency';
import { formatResults } from './results/utils';
import {
  FORM_NAME, BOOKING_STEPS, QUOTING_TYPES, ORDER_TYPES,
} from './constants';
import * as apis from './apis';
import {
  init as initResults, save, call, markAsPaid,
} from './results/actions';
import {
  initCategories,
  initShippers,
  search,
  cancel,
  preBookPrepare,
  destroyPreBook,
  setBookingStep,
  preBook,
} from './actions';
import { categories } from './components/data/trucksCategories';
import { paySuccess } from '../payment/actions';
import {
  getFormattedSearchSummary,
  getFormattedSearchRequestForSingleShipment,
  getFormattedSearchRequestForDistributedShipment,
} from './model';
import {
  getShipper,
  getSender,
  getRecievers,
  getPickup,
  getDropoffs,
  getQuotingOptions,
} from './selectors';

function* getInitialData() {
  try {
    const morphed = categories.reduce((output, category) => {
      output[category._id] = {
        ...category,
        name: I18n.str(`truck_${category.name}`, category.name),
        childs:
          category && category.childs
            ? category.childs.map((child) => ({
              ...child,
              name: I18n.str(child.name, child.name),
            }))
            : {},
      };
      return output;
    }, {});
    yield put(initCategories(morphed));
  } catch (e) {
    console.log(e);
  }
}
// @deprecate
function* updateDimensions(action) {
  if (action.meta.form !== FORM_NAME || !action.meta.field.includes('.item')) return;
  const state = yield select();
  const categoryId = state.form.newOrder.values.category;
  const category = state.app.order.categories[categoryId];

  const currentItem = category
    && category.childs
    && category.childs.filter((child) => action.payload === child._id)[0];

  if (currentItem === undefined) {
    return;
  }
  const { dimensions, weight, stackable } = currentItem;
  yield put(change(FORM_NAME, 'equipment.capacity', weight));

  const {
    width, length, height, maxQuantity = 10, show,
  } = dimensions;

  // We should listen only for the item
  const currentIndex = action.meta.field.replace(/\D/g, '');
  yield put(change(FORM_NAME, `load[${currentIndex}].quantity`, 1));
  if (length > 0) {
    yield put(change(FORM_NAME, `load[${currentIndex}].length`, length));
    yield put(change(FORM_NAME, `load[${currentIndex}].height`, height));
    yield put(change(FORM_NAME, `load[${currentIndex}].width`, width));
    yield put(change(FORM_NAME, `load[${currentIndex}].dimsUnit`, 'CM'));
    yield put(change(FORM_NAME, `load[${currentIndex}].maxQuantity`, maxQuantity));
    yield put(change(FORM_NAME, `load[${currentIndex}].hideDims`, show === false));
    yield put(change(FORM_NAME, `load[${currentIndex}].predefined`, true));

    yield put(change(FORM_NAME, `load[${currentIndex}].weight`, weight.value));
    yield put(change(FORM_NAME, `load[${currentIndex}].weightUnit`, 'KG'));
    yield put(change(FORM_NAME, `load[${currentIndex}].stackable`, stackable));
  } else {
    yield put(change(FORM_NAME, `load[${currentIndex}].predefined`, false));
    yield put(change(FORM_NAME, `load[${currentIndex}].pallets`, currentItem.pallets));
  }
}

function* fillAddressData(action) {
  if (
    !action.payload
    || action.meta.form !== FORM_NAME
    || !action.meta.field.includes('.locationName')
  ) return;
  const state = yield select();
  const { addresses } = state.app.addressBook;
  const isPickup = action.meta.field.includes('pickup');
  const filteredAddresses = addresses.filter((address) => address.id === action.payload.value);
  const fieldNamePrefix = isPickup ? 'pickup.' : action.meta.field.replace('locationName', '');
  // It's not a new adderss
  if (filteredAddresses.length) {
    const currentAddress = filteredAddresses[0];
    const {
      address, mobile, email, contact,
    } = currentAddress;

    yield put(change(FORM_NAME, `${fieldNamePrefix}phone`, mobile));
    yield put(change(FORM_NAME, `${fieldNamePrefix}email`, email));
    yield put(change(FORM_NAME, `${fieldNamePrefix}contact`, contact));
    yield put(change(FORM_NAME, `${fieldNamePrefix}address`, address));
  }
}

function* doSearch(context, action) {
  if (action.payload.name !== FORM_NAME) return;
  yield put(startSubmit(FORM_NAME));

  // TODO Create an entry in the address book for the newely created addresses
  try {
    const state = yield select();
    const { values } = action.payload;
    let formattedSearch;
    let results;

    const shipper = getShipper(state);
    const sender = getSender(state);
    const recievers = getRecievers(state);
    const pickup = getPickup(state);
    const dropoffs = getDropoffs(state);
    const quotingOptions = getQuotingOptions(state);
    const currency = getCurrency(state);

    const summary = getFormattedSearchSummary(
      quotingOptions,
      sender,
      recievers,
      pickup,
      dropoffs,
      currency,
    );

    yield put(
      startLoading('SEARCH_STARTED', {
        urls: ['/', '/search'],
      }),
    );

    if (dropoffs.length > 1) {
      formattedSearch = getFormattedSearchRequestForDistributedShipment(
        quotingOptions,
        shipper,
        sender,
        recievers,
        pickup,
        dropoffs,
        null,
        currency,
      );
      results = yield apis.findDistributedShipmentQuotes(
        formattedSearch,
        getCurrentAuthHeader(state),
      );
    } else {
      formattedSearch = getFormattedSearchRequestForSingleShipment(
        quotingOptions,
        shipper,
        sender,
        recievers,
        pickup,
        dropoffs,
        null,
        currency,
      );
      results = yield apis.findSingleShipmentQuotes(formattedSearch, getCurrentAuthHeader(state));
    }

    const quotes = formatResults(results);

    yield put(stopSubmit(FORM_NAME));
    if (quotes.length === 0) {
      yield put(doneLoading('SEARCH_STARTED'));
      alert(results.message);
      return;
    }
    yield put(initResults(summary, quotes));
    yield context.history.push('/results');
  } catch (e) {
    yield put(stopSubmit(FORM_NAME));

    if (e.response.data.message) {
      const { message } = e.response.data;
      if (message.includes('There is not enough time')) {
        yield put(
          showNotification(
            I18n.str(
              'searchNoTimeEnough',
              'There is no enough time between the pickup and delivery.',
            ),
            'error',
          ),
        );
      }
    } else {
      alert('Something went wrong while trying to perform your search!! Please try again');
    }
  }
  yield put(doneLoading('SEARCH_STARTED'));
}

function* doSave(context, action) {
  const {
    payload: { shipmentId, itineraryId },
  } = action;
  yield context.history.push(`/shipment/view/${shipmentId}/${itineraryId}`);
}

function* capturePaid(context, action) {
  yield put(startLoading('BOOKING'));

  const state = yield select();
  const {
    id,
    token,
    cardLastDigits,
    method,
    expiryDate,
    amount,
    currency,
    securityCode,
    userCardType,
  } = action.payload;
  const { profileId } = getCurrentProfile(state);

  const shipment = yield shippingClient.Get(`/api/shipments/shipment/Shipment_${id}`).send();
  try {
    let type = userCardType;
    if (!userCardType) {
      type = method;
      if (method.includes('**')) {
        type = creditCardType(method[0])[0].type.toUpperCase();
        if (type === 'american-express') type = 'AMEX';
      }
    }

    const paymentInfo = {
      token,
      method: type,
      cardLastDigits,
      expiryDate,
    };
    const { name, email, phone_number } = state.circe.authentication.userData;

    const authPayload = {
      amount,
      currency,
      customerEmail: email.replace('+', ''),
      customerName: name,
      eci: 'ECOMMERCE',
      language: 'en',
      merchantExtra: 'dummyValue',
      merchantReference: shipment.shipmentId.replace('Shipment_', ''),
      orderDescription: shipment.shipmentId.replace('Shipment_', ''),
      paymentOption: type,
      phoneNumber: phone_number.replace('+', '00'),
      settlementReference: '',
      tokenName: token,
    };
    if (securityCode) {
      authPayload.cardSecurityCode = securityCode;
    }
    yield put(setBookingStep(BOOKING_STEPS.AUTHORIZATION));
    const { errorMessage, successful } = shippingClient
      .Post('/api/payfort/authorize')
      .withData(authPayload)
      .send();

    if (successful) {
      yield put(setBookingStep(BOOKING_STEPS.CONFIRMATION));

      yield put(
        preBookPrepare(
          errorMessage,
          shipment.shipmentId,
          shipment.itineraryId,
          profileId,
          paymentInfo,
        ),
      );
      yield put(markAsPaid(id, token, cardLastDigits, method, expiryDate));
      if (!errorMessage) {
        yield doBook(context);
      }
    } else {
      alert(errorMessage);
      yield put(setBookingStep(BOOKING_STEPS.NEW));
      yield context.history.push('/');
    }
    // We need to mark the quote as paid
  } catch (e) {
    Logger.exception(e, {
      tags: '[Payments]',
    });
    alert('Something went wrong, please try again ');
    yield put(setBookingStep(BOOKING_STEPS.NEW));
    yield context.history.push('/');
    console.error('Error capturing payment ', e);
  }
  yield put(doneLoading('BOOKING'));
}
function* doBook(context, action) {
  yield put(startLoading('BOOKING'));
  // The id returned from payfort callback
  let redirectedShipmentId = null;

  try {
    if (action) {
      // TODO Should be prebook checks
      const {
        payload: { response, message, shipmentId },
      } = action;
      redirectedShipmentId = `Shipment_${shipmentId}`;
      if (response === 'AUTH_FAILED') {
        throw new Error(message);
      }
    }

    yield put(setBookingStep(BOOKING_STEPS.BOOKING));
    const state = yield select();
    const { url, ...rest } = state.app.order.preBook;
    if (!rest.shipperId) return;

    if (rest.paymentInfo.method) {
      rest.paymentInfo.cardType = rest.paymentInfo.method;
      delete rest.paymentInfo.method;
    }

    yield put(destroyPreBook());
    const {
      shipmentId,
      shipperId,
      paymentInfo: {
        cardLastDigits, token, method: type, expiryDate,
      },
    } = rest;

    if (redirectedShipmentId !== null && shipmentId !== redirectedShipmentId) {
      return;
    }

    const response = yield shippingClient
      .Post('/api/shipments/book')
      .withData(rest)
      .send();

    if (typeof response === typeof undefined) {
      throw new Error('Server Error!! We were not able to complete your order');
    }
    if (!response.orderId) {
      yield put(setBookingStep(BOOKING_STEPS.FAILED));
      yield put(doneLoading('BOOKING'));
      throw new Error('Something went wrong with your shipment');
    }
    yield put(setBookingStep(BOOKING_STEPS.BOOKED));

    // check wheather to save the card or not
    const cards = yield shippingClient.Get(`/api/users/creditcards/${shipperId}`).send();

    const filteredCards = cards
      ? cards.filter((card) => card.cardLastDigits === cardLastDigits)
      : [];
    if (!filteredCards.length) {
      const cardPayload = {
        token,
        cardType: type,
        cardLastDigits,
        expiryDate,
      };
      yield shippingClient
        .Post(`/api/users/creditcards/${shipperId}`)
        .withData(cardPayload)
        .send();
    }
  } catch (e) {
    Logger.exception(e, {
      tags: '[Booking]',
    });
    const message = e.message || 'We were not able to book your shipment right now!! Please try again later';
    alert(message);
  }

  yield put(doneLoading('BOOKING'));
  // Need to finalize and clear
}
function* doCancel(context, action) {
  const {
    payload: { shipmentId },
  } = action;

  const state = yield select();
  const authHeaders = getCurrentAuthHeader(state);
  yield put(
    startLoading('REFUNDING', {
      urls: ['/shipments', '/my-shipments'],
    }),
  );
  try {
    const cancelRequest = yield shippingClient
      .Post(`/api/shipments/${shipmentId}/cancelRequest`)
      .send();
    const cancelConfirm = yield shippingClient
      .Post(`/api/shipments/${shipmentId}/cancelConfirm`)
      .send();
    if (cancelConfirm !== 'Success') {
      alert('Something went wrong, please try again ');
    }
  } catch (e) {
    alert('Something went wrong, please try again ');
    Logger.exception(e);
  }

  yield put(doneLoading('REFUNDING'));
}

function* doPreBook(context, { payload }) {
  const { quote, payment, orderType } = payload;

  const state = yield select();
  const headers = getCurrentAuthHeader(state);
  const profile = getCurrentProfile(state);

  yield put(paying());
  const preBookPayload = {
    quoteId: quote.quoteId,
    shipperId: quote.shipperId || profile.profileId,
  };

  const preBookResp = yield shippingClient
    .Post('/api/shipments/prebook')
    .withData(preBookPayload)
    .send();

  yield put(
    pay({
      ...payment,
      orderId: preBookResp.orderId,
      extra: {
        shipmentId: quote.shipmentId,
        itineraryId: quote.itineraryId,
        quoteId: quote.quoteId,
        orderType,
      },
      shipperId: quote.shipperId || profile.profileId,
      orderDescription: `Order ${preBookResp.orderId}`,
    }),
  );
}

function* doRedirectOnPreBook(context, { payload }) {
  const {
    orderId,
    extra: { orderType },
  } = payload;
  if (!orderId) {
    return;
  }
  if (orderType !== ORDER_TYPES.BATCH) context.history.push(generatePath(ORDER_ROUTES.ROOT, { oId: orderId }));
}

function* watcher(context) {
  yield all([
    takeLatest(bootstrap().type, getInitialData),
    takeLatest(changeLocale().type, getInitialData),
    takeLatest(initLocale().type, getInitialData),
    // takeEvery(change().type, updateDimensions),
    takeEvery(change().type, fillAddressData),
    takeEvery(preBook().type, doPreBook, context),
    takeEvery(doSubmit().type, doSearch, context),
    takeEvery(save().type, doSave, context),
    takeEvery(paid().type, capturePaid, context),
    takeEvery(cancel().type, doCancel, context),
    takeEvery(authorizedCallback().type, doBook, context),
    takeEvery(paySuccess().type, doRedirectOnPreBook, context),
  ]);
}

export default watcher;
